import { ExpandLess, ExpandMore } from "@mui/icons-material"
import CloseIcon from "@mui/icons-material/Close"
import PhotoIcon from "@mui/icons-material/Photo"
import {
    Box,
    CardMedia,
    Collapse,
    Grid,
    IconButton,
    Typography,
} from "@mui/material"
import { ReactElement, useState } from "react"
import { Control, FieldValues } from "react-hook-form"

import { S3_TAG_REGEX } from "../../../../../constants/patterns"
import { useScreenSize } from "../../../../../hooks/useScreenSize"
import {
    BORDER_RADIUS,
    GRID_SPACING,
} from "../../../../../themes/defaultThemeParams"
import {
    getAttachmentDescriptionRegisterName,
    getAttachmentRestrictedRegisterName,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import AppTooltip from "../../../../helper/appTooltip"
import ValidatedSwitch from "../../../../inputs/validated/validatedSwitch"
import ValidatedTextField from "../../../../inputs/validated/validatedTextField"
import { AppCard } from "../../../../surfaces/appCard"
import DisplayIf from "../../../../wrappers/displayIf"
import DisplayIfElse from "../../../../wrappers/displayIfElse"

interface IAttachmentMetaFormprops {
    base64Image?: string
    fileName: string
    control: Control<FieldValues, unknown>
    onRemove: () => void
    state: "uploaded" | "staged"
    canEditDescription?: boolean
    description?: string
    onToggle?: () => void
    disabled: boolean
}

interface IAttachmentFieldsProps {
    control: Control<FieldValues, unknown>
    descriptionRegisterName: string
    restrictedRegisterName: string
    canEditDescription: boolean
    description?: string
    onToggle?: () => void
    disabled: boolean
}

/**
 * Displays a switch for altering an attachments published state.
 * Also displays a textfield to edit the description, provided `canEditDescription` is true,
 * else displays the provided `description`
 */
const AttachmentFields = ({
    control,
    descriptionRegisterName,
    restrictedRegisterName,
    canEditDescription,
    disabled,
    description = undefined,
    onToggle = undefined,
}: IAttachmentFieldsProps): ReactElement => {
    const MAX_DESCRIPTION_CHARACTERS = 256
    const RESTRICTED_TOOLTIP =
        "Restricted attachments are only visible to DNOs, device evaluators and ENA system admins. " +
        "Un-restricted attachments are publilciably  available once published"

    return (
        <>
            <DisplayIfElse
                condition={canEditDescription}
                displayIfTrue={
                    <Grid item xs={6} sm={8.5} md={5}>
                        <ValidatedTextField
                            label="Description"
                            placeholder="Add a description. 256 characters max"
                            required={false}
                            registerName={descriptionRegisterName}
                            control={control}
                            disabled={disabled}
                            customRules={{
                                maxLength: {
                                    value: MAX_DESCRIPTION_CHARACTERS,
                                    message: `Max permitted characters: 
                                    ${MAX_DESCRIPTION_CHARACTERS}`,
                                },
                                pattern: {
                                    value: S3_TAG_REGEX,
                                    message:
                                        "Special characters are not allowed in the description",
                                },
                            }}
                        />
                    </Grid>
                }
                displayIfFalse={
                    <Grid item xs={6} sm={8.5} md={5}>
                        {description ? (
                            <Typography>{description}</Typography>
                        ) : (
                            <Typography fontStyle="italic">
                                No description provided
                            </Typography>
                        )}
                    </Grid>
                }
            />
            <Grid item xs={6} sm={3.5} md={2.5}>
                <AppTooltip title={RESTRICTED_TOOLTIP}>
                    <Box margin={0} padding={0}>
                        <ValidatedSwitch
                            label="Restricted"
                            labelPlacement="start"
                            registerName={restrictedRegisterName}
                            control={control}
                            onToggle={onToggle}
                            disabled={disabled}
                        />
                    </Box>
                </AppTooltip>
            </Grid>
        </>
    )
}

/** Displays an attachment form. Allows user to edit restricted state and description */
const AttachmentMetaForm = ({
    fileName,
    control,
    onRemove,
    state,
    disabled: disbaled,
    base64Image = undefined,
    canEditDescription = true,
    description = undefined,
    onToggle = undefined,
}: IAttachmentMetaFormprops): ReactElement => {
    const descriptionRegisterName =
        getAttachmentDescriptionRegisterName(fileName)
    const restrictedRegisterName = getAttachmentRestrictedRegisterName(fileName)

    const [expanded, setExpaned] = useState<boolean>(false)

    const { isTabletOrMob } = useScreenSize()

    return (
        <AppCard
            sx={{
                backgroundColor:
                    state === "staged"
                        ? "background.contentBackground"
                        : "background.successBackground",
                borderRadius: BORDER_RADIUS * 5,
                paddingX: GRID_SPACING * 2,
            }}
        >
            <Grid container spacing={GRID_SPACING} alignItems="center">
                <Grid item xs={3.5} sm={2.5} md={1}>
                    {base64Image ? (
                        <CardMedia
                            component="img"
                            sx={{
                                borderRadius: "10px",
                                minWidth: "70px",
                                width: "70px",
                                height: "70px",
                            }}
                            src={base64Image}
                        />
                    ) : (
                        <PhotoIcon sx={{ fontSize: 70 }} />
                    )}
                </Grid>
                <Grid item xs={4} sm={7} md={2}>
                    <Typography
                        fontWeight={700}
                        sx={{
                            overflowWrap: "break-word",
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                        }}
                    >
                        {fileName}
                    </Typography>
                </Grid>
                <DisplayIf condition={!isTabletOrMob}>
                    <AttachmentFields
                        control={control}
                        descriptionRegisterName={descriptionRegisterName}
                        restrictedRegisterName={restrictedRegisterName}
                        canEditDescription={canEditDescription}
                        description={description ?? ""}
                        onToggle={onToggle}
                        disabled={disbaled}
                    />
                </DisplayIf>
                <Grid item xs={2} sm={1.5} md={1}>
                    <AppTooltip title="Remove attachment">
                        <span>
                            <IconButton onClick={onRemove} disabled={disbaled}>
                                <CloseIcon color="error" fontSize="large" />
                            </IconButton>
                        </span>
                    </AppTooltip>
                </Grid>
                <DisplayIf condition={isTabletOrMob}>
                    <Grid item xs={2} sm={1} md={2}>
                        <IconButton onClick={() => setExpaned(!expanded)}>
                            {expanded ? (
                                <ExpandLess fontSize="large" />
                            ) : (
                                <ExpandMore fontSize="large" />
                            )}
                        </IconButton>
                    </Grid>
                </DisplayIf>
            </Grid>
            <DisplayIf condition={isTabletOrMob}>
                <Collapse
                    in={expanded}
                    sx={{
                        marginTop: 4,
                    }}
                >
                    <Grid container spacing={GRID_SPACING} alignItems="center">
                        <AttachmentFields
                            control={control}
                            descriptionRegisterName={descriptionRegisterName}
                            restrictedRegisterName={restrictedRegisterName}
                            canEditDescription={canEditDescription}
                            description={description ?? ""}
                            onToggle={onToggle}
                            disabled={disbaled}
                        />
                    </Grid>
                </Collapse>
            </DisplayIf>
        </AppCard>
    )
}

AttachmentFields.defaultProps = {
    onToggle: undefined,
    description: undefined,
}

AttachmentMetaForm.defaultProps = {
    canEditDescription: true,
    description: undefined,
    onToggle: undefined,
    base64Image: undefined,
}

export default AttachmentMetaForm
